/** Storage compartilhado para excursões (guia e cliente) */
import { addNotification } from './notificationStorage';

export const EXCURSOES_STORAGE_KEY = 'naviex-excursoes';

export interface DayConfigExcursao {
  city?: string;
  maxPessoas: string;
  quorumMinimo: string;
  valorPorPessoa: string;
  inscritos?: string;
  horarioSaida: string;
  pontoPartida: string;
  gps: string;
  experiences: number[]; // IDs das experiências (1 a 4)
  hasTransport?: boolean;
  vagasComTransporte?: string;
  valorVagaTransporte?: string;
}

export type StatusExcursao = 'em formação' | 'confirmada' | 'concluída' | 'cancelada';

/** Data local no formato YYYY-MM-DD (comparação segura com `Excursao.date`). */
function excursaoHojeYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Status derivado: cancelada explícita; senão, data + quórum.
 * - Em formação: data hoje ou futura e ainda não atingiu o quórum (ou dia passou sem quórum).
 * - Confirmada: data hoje ou futura e inscritos ≥ quórum.
 * - Concluída: data anterior a hoje, quórum tinha sido atingido (excursão realizável / realizada).
 */
export function resolveExcursaoStatus(exc: Pick<Excursao, 'status' | 'date' | 'quorumMinimo' | 'inscritos'>): StatusExcursao {
  if (exc.status === 'cancelada') return 'cancelada';
  const quorum = Number(exc.quorumMinimo) || 0;
  const inscritos = Number(exc.inscritos) || 0;
  const dateStr = (exc.date || '').slice(0, 10);
  const hoje = excursaoHojeYmd();

  if (dateStr < hoje) {
    if (quorum > 0 && inscritos >= quorum) return 'concluída';
    return 'em formação';
  }

  if (quorum > 0 && inscritos >= quorum) return 'confirmada';
  return 'em formação';
}

export type MotivoCancelamentoExcursao = 'meteorologia' | 'quorum_insuficiente' | 'outro';

/** Grupos de reserva na mesma excursão (para dashboard admin). */
export interface ReservaGrupoExcursao {
  id?: string;
  responsavelEmail?: string;
  responsavelNome?: string;
  telefone?: string;
  quantidadePessoas: number;
  /** Valor total pago por este grupo (separado do agregado da excursão). */
  valorTotalVenda?: number;
}

export interface Excursao {
  id: string;
  date: string; // Data única da excursão (YYYY-MM-DD)
  status: StatusExcursao;
  // Configuração do dia
  city: string;
  maxPessoas: number;
  quorumMinimo: number;
  valorPorPessoa: number;
  valorGuia?: number;
  inscritos: number; // Total de pessoas inscritas
  horarioSaida: string;
  pontoPartida: string;
  gps: string;
  experiences: number[]; // IDs das experiências (1 a 4 no admin legado, ou hashId(UUID) no cliente)
  /** IDs de admin das experiências (UUID), para título/merge no app cliente sem depender só do índice legado. */
  experienceAdminIds?: string[];
  // Transporte
  hasTransport: boolean;
  vagasComTransporte?: number;
  valorVagaTransporte?: number;
  quorumMinimoTransporte?: number;
  inscritosComTransporte?: number;
  // Identificação
  guiaId?: string; // ID do guia (se criada por guia)
  guiaEmail?: string; // Email do guia vinculado (quando definido pelo admin)
  clienteIds?: string[]; // IDs dos clientes que compraram
  criadaPor: 'guia' | 'cliente' | 'admin'; // Quem criou
  // Metadados
  title?: string; // Título da excursão (gerado das experiências)
  subtitle?: string; // Subtítulo (gerado das experiências)
  imageUrl?: string; // Imagem (primeira experiência)
  createdAt: string; // ISO string
  /** ID da excursão vinculada (COM↔SEM transporte) */
  excursaoVinculadaId?: string;
  /** Total arrecadado por inscrição (para admin) */
  arrecadadoPorInscricao?: number;
  /** Total arrecadado por grupo (para admin) */
  arrecadadoPorGrupo?: number;
}

export type ExcursoesMap = Record<string, Excursao>;

/**
 * Excursões seed legadas de desenvolvimento (`mock-exc-*` / `mock-exp-*`).
 * Não devem aparecer na home nem em Minha programação.
 */
export function isExcursaoClienteDevBootstrapMock(exc: Pick<Excursao, 'id' | 'experienceAdminIds'>): boolean {
  if (String(exc.id ?? '').trim().startsWith('mock-exc-')) return true;
  const aids = exc.experienceAdminIds;
  if (Array.isArray(aids) && aids.some((x) => String(x).startsWith('mock-exp-'))) return true;
  return false;
}

/** Remove só entradas de mock de dev do `localStorage` (preserva excursões reais / rascunho local). */
export function stripClienteBootstrapMocksFromExcursaoStorage(): void {
  const cur = getExcursoesFromStorage();
  const next: ExcursoesMap = {};
  let removed = false;
  for (const [id, ex] of Object.entries(cur)) {
    if (isExcursaoClienteDevBootstrapMock(ex)) {
      removed = true;
      continue;
    }
    next[id] = ex;
  }
  if (removed) {
    saveExcursoesToStorage(next);
    try {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('excursao-updated'));
    } catch {
      /* ignore */
    }
  }
}

export function getExcursoesFromStorage(): ExcursoesMap {
  try {
    const s = localStorage.getItem(EXCURSOES_STORAGE_KEY);
    return s ? (JSON.parse(s) as ExcursoesMap) : {};
  } catch {
    return {};
  }
}

export function saveExcursoesToStorage(data: ExcursoesMap): void {
  try {
    const normalized = Object.fromEntries(
      Object.entries(data).map(([id, ex]) => [id, { ...ex, status: resolveExcursaoStatus(ex) }])
    ) as ExcursoesMap;
    localStorage.setItem(EXCURSOES_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // ignore
  }
}

export function addExcursaoToStorage(excursao: Excursao): void {
  const statusResolvido = resolveExcursaoStatus(excursao);
  const excursoes = getExcursoesFromStorage();
  excursoes[excursao.id] = { ...excursao, status: statusResolvido };
  saveExcursoesToStorage(excursoes);
  if (excursao.criadaPor === 'admin' && statusResolvido === 'confirmada') {
    addNotification({
      toEmail: 'guia@testando.com',
      type: 'success',
      title: 'Excursão confirmada por quórum',
      description: `A excursão de ${excursao.date} atingiu o quórum e foi confirmada.`,
      metadata: { excursaoId: excursao.id },
    });
  }
}

export function updateExcursaoInStorage(excursao: Excursao): void {
  const excursoes = getExcursoesFromStorage();
  if (excursoes[excursao.id]) {
    const anterior = excursoes[excursao.id];
    const statusResolvido = resolveExcursaoStatus(excursao);
    excursoes[excursao.id] = { ...excursao, status: statusResolvido };
    saveExcursoesToStorage(excursoes);
    if (
      excursao.criadaPor === 'admin' &&
      anterior.status !== 'confirmada' &&
      statusResolvido === 'confirmada'
    ) {
      addNotification({
        toEmail: 'guia@testando.com',
        type: 'success',
        title: 'Excursão confirmada por quórum',
        description: `A excursão de ${excursao.date} atingiu o quórum e foi confirmada.`,
        metadata: { excursaoId: excursao.id },
      });
    }
  }
}

export function removeExcursaoFromStorage(excursaoId: string): void {
  const excursoes = getExcursoesFromStorage();
  delete excursoes[excursaoId];
  saveExcursoesToStorage(excursoes);
}

export function getExcursoesByDate(date: string): Excursao[] {
  const excursoes = getExcursoesFromStorage();
  return Object.values(excursoes).filter(excursao => excursao.date === date);
}

export function getExcursoesByDateRange(startDate: string, endDate: string): Excursao[] {
  const excursoes = getExcursoesFromStorage();
  
  // Comparar datas como strings (formato YYYY-MM-DD) para evitar problemas de timezone
  // As datas já estão no formato YYYY-MM-DD, então podemos comparar diretamente
  return Object.values(excursoes).filter(excursao => {
    // Comparar strings diretamente (YYYY-MM-DD)
    return excursao.date >= startDate && excursao.date <= endDate;
  });
}
